import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import { adminCreatePackage, adminGetPackage, adminUpdatePackage } from '../../api/packages';
import { todayIso } from '../../shared/data/common';
import { SmartImg } from '../../shared/components/Layout';
import { usePackages } from '../packages/PackagesContext';
import { adminThemes } from '../packages/data';
import AdminShell, { useAdminAccess } from './AdminShell';
import { categories, categoryInfo, emptyDay, emptyDeparture, emptyForm, toForm, toRequest } from './packageForm';

export function Field({ label, hint, wide, children }) {
  return (
    <label className={`field ${wide ? 'wide' : ''}`}>
      <span className="field-label">{label}</span>
      {children}
      {hint && <span className="field-hint">{hint}</span>}
    </label>
  );
}

export function Section({ title, hint, children }) {
  return (
    <fieldset className="panel admin-section">
      <legend>{title}</legend>
      {hint && <p className="muted small">{hint}</p>}
      {children}
    </fieldset>
  );
}

export default function PackageFormPage() {
  const { packageId } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { auth } = useAuth();
  const hasAccess = useAdminAccess();
  const { reload: reloadSite } = usePackages();
  const [form, setForm] = useState(() => emptyForm(params.get('category')));
  const [loading, setLoading] = useState(Boolean(packageId));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!packageId || !hasAccess) return;
    let active = true;
    adminGetPackage(auth.accessToken, packageId)
      .then((pkg) => active && setForm(toForm(pkg)))
      .catch((requestError) => active && setError(requestError.message))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [packageId, hasAccess, auth?.accessToken]);

  const set = (field) => (event) => {
    const { type, checked, value } = event.target;
    setForm((f) => ({ ...f, [field]: type === 'checkbox' ? checked : value }));
  };
  const setRow = (key, index, field) => (event) => setForm((f) => ({
    ...f, [key]: f[key].map((row, i) => (i === index ? { ...row, [field]: event.target.value } : row)),
  }));
  const addRow = (key, make) => setForm((f) => ({ ...f, [key]: [...f[key], make()] }));
  const removeRow = (key, index) => setForm((f) => ({ ...f, [key]: f[key].filter((_, i) => i !== index) }));

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      const body = toRequest(form);
      const saved = packageId
        ? await adminUpdatePackage(auth.accessToken, packageId, body)
        : await adminCreatePackage(auth.accessToken, body);
      reloadSite();
      navigate(`/admin/packages?category=${saved.category}`, {
        state: { message: `"${saved.title}" was saved${saved.isPublished ? ' and is live on the website' : ' as a draft'}.` },
      });
    } catch (requestError) {
      setError(requestError.message);
      window.scrollTo(0, 0);
    } finally {
      setBusy(false);
    }
  };

  const info = categoryInfo[form.category];
  const maxDays = Number(form.nights) + 1;
  const isGroup = form.category === 'Group';
  const isCustomized = form.category === 'Customized';

  return (
    <AdminShell title={packageId ? `Edit ${info.label.toLowerCase()}` : `New ${info.label.toLowerCase()}`} subtitle={info.blurb}>
      {loading ? <p role="status">Loading package…</p> : (
        <form className="admin-form" onSubmit={submit}>
          {error && <p className="api-message error" role="alert">{error}</p>}

          <div className="segmented" role="radiogroup" aria-label="Package type">
            {categories.map((c) => (
              <label key={c} className={form.category === c ? 'active' : ''}>
                <input type="radio" name="category" value={c} checked={form.category === c} onChange={set('category')} />
                {categoryInfo[c].label}
              </label>
            ))}
          </div>

          <Section title="Basic details">
            <div className="form-grid">
              <Field label="Package title" wide>
                <input required minLength="3" maxLength="150" value={form.title} onChange={set('title')} placeholder="e.g. Kerala Backwaters & Hills" />
              </Field>
              <Field label="Main destination" hint="Used for search and the header menus">
                <input required minLength="2" maxLength="100" value={form.destination} onChange={set('destination')} placeholder="e.g. Kerala" />
              </Field>
              <Field label="Region">
                <select value={form.isInternational ? 'intl' : 'india'} onChange={(e) => setForm((f) => ({ ...f, isInternational: e.target.value === 'intl' }))}>
                  <option value="india">India</option>
                  <option value="intl">International</option>
                </select>
              </Field>
              <Field label="Nights">
                <input required type="number" min="1" max="60" value={form.nights} onChange={set('nights')} />
              </Field>
              {!isGroup && (
                <Field label="Theme" hint="Pilgrimage packages are listed under Religious Tours">
                  <select value={form.theme} onChange={set('theme')}>{adminThemes.map((t) => <option key={t}>{t}</option>)}</select>
                </Field>
              )}
              <Field label="Image URL" wide>
                <input type="url" maxLength="500" value={form.imageUrl} onChange={set('imageUrl')} placeholder="https://…" />
              </Field>
              {form.imageUrl && <div className="wide admin-image-preview"><SmartImg src={form.imageUrl} alt="Package preview" /></div>}
              <Field label="Overview" wide>
                <textarea rows="3" maxLength="2000" value={form.overview} onChange={set('overview')} placeholder="A short introduction shown on the package page" />
              </Field>
            </div>
          </Section>

          <Section title="Pricing" hint="Prices are in INR, excluding GST and TCS.">
            <div className="form-grid three">
              <Field label={info.priceLabel}>
                <input required type="number" min="1" step="1" value={form.pricePerAdult} onChange={set('pricePerAdult')} />
              </Field>
              <Field label="Child price (2–11 yrs)" hint="Leave blank to charge 70% of the adult price">
                <input type="number" min="0" step="1" value={form.childPrice} onChange={set('childPrice')} />
              </Field>
              <Field label="Original price" hint="Shown struck through, for a discount">
                <input type="number" min="1" step="1" value={form.originalPrice} onChange={set('originalPrice')} />
              </Field>
            </div>
          </Section>

          {isGroup && (
            <Section title="Group departures" hint="Each departure is a fixed date customers can book seats on. Leave the price blank to use the package price.">
              <div className="repeater">
                {form.departures.map((d, i) => (
                  <div key={i} className="repeater-row departures">
                    <Field label={`Departure ${i + 1} date`}>
                      <input required type="date" min={packageId ? undefined : todayIso()} value={d.date} onChange={setRow('departures', i, 'date')} />
                    </Field>
                    <Field label="Seats">
                      <input required type="number" min="1" max="500" value={d.seats} onChange={setRow('departures', i, 'seats')} />
                    </Field>
                    <Field label="Price per adult (optional)">
                      <input type="number" min="1" value={d.pricePerAdult} onChange={setRow('departures', i, 'pricePerAdult')} />
                    </Field>
                    <button type="button" className="btn btn-outline btn-sm danger" disabled={form.departures.length === 1}
                      onClick={() => removeRow('departures', i)} aria-label={`Remove departure ${i + 1}`}>Remove</button>
                  </div>
                ))}
                <button type="button" className="btn btn-outline btn-sm" onClick={() => addRow('departures', emptyDeparture)}>+ Add departure</button>
              </div>
              <div className="form-grid three">
                <Field label="Minimum group size"><input type="number" min="1" max="500" value={form.minGroupSize} onChange={set('minGroupSize')} /></Field>
                <Field label="Maximum group size"><input type="number" min="1" max="500" value={form.maxGroupSize} onChange={set('maxGroupSize')} /></Field>
                <label className="check align-end">
                  <input type="checkbox" checked={form.tourManagerIncluded} onChange={set('tourManagerIncluded')} />
                  <span>Tour manager travels with the group</span>
                </label>
              </div>
            </Section>
          )}

          {isCustomized && (
            <Section title="What customers can customise" hint="One option per line, e.g. “Hotel category: 3★ / 4★ / 5★”.">
              <Field label="Customisation options" wide>
                <textarea rows="4" value={form.customizationOptions} onChange={set('customizationOptions')} />
              </Field>
            </Section>
          )}

          <Section title="Route & stay" hint="One entry per line.">
            <div className="form-grid">
              <Field label="Cities & nights" hint="e.g. Munnar 2N">
                <textarea rows="4" value={form.cities} onChange={set('cities')} />
              </Field>
              <Field label="Hotels" hint="In the same order as the cities">
                <textarea rows="4" value={form.hotels} onChange={set('hotels')} />
              </Field>
            </div>
          </Section>

          <Section title="Inclusions" hint="One entry per line.">
            <div className="form-grid three">
              <Field label="Highlights" hint="Short tags on the package card, e.g. Flights, Hotel, Meals">
                <textarea rows="5" value={form.highlights} onChange={set('highlights')} />
              </Field>
              <Field label="Inclusions"><textarea rows="5" value={form.inclusions} onChange={set('inclusions')} /></Field>
              <Field label="Exclusions"><textarea rows="5" value={form.exclusions} onChange={set('exclusions')} /></Field>
            </div>
          </Section>

          <Section title="Day-wise itinerary" hint={`A ${form.nights}-night package can have up to ${maxDays} days.`}>
            <div className="repeater">
              {form.itinerary.map((d, i) => (
                <div key={i} className="repeater-row itinerary">
                  <span className="day-badge">Day {i + 1}</span>
                  <div className="form-grid">
                    <Field label={`Day ${i + 1} title`}>
                      <input maxLength="200" value={d.title} onChange={setRow('itinerary', i, 'title')} placeholder="e.g. Arrive in Kochi — drive to Munnar" />
                    </Field>
                    <Field label="Meals">
                      <input maxLength="100" value={d.meals} onChange={setRow('itinerary', i, 'meals')} placeholder="e.g. Breakfast, Dinner" />
                    </Field>
                    <Field label="Description" wide>
                      <textarea rows="2" maxLength="1000" value={d.description} onChange={setRow('itinerary', i, 'description')} />
                    </Field>
                  </div>
                  <button type="button" className="btn btn-outline btn-sm danger" disabled={form.itinerary.length === 1}
                    onClick={() => removeRow('itinerary', i)} aria-label={`Remove day ${i + 1}`}>Remove</button>
                </div>
              ))}
              <button type="button" className="btn btn-outline btn-sm" disabled={form.itinerary.length >= maxDays}
                onClick={() => addRow('itinerary', emptyDay)}>+ Add day</button>
            </div>
          </Section>

          <div className="panel admin-submit">
            <label className="check">
              <input type="checkbox" checked={form.isPublished} onChange={set('isPublished')} />
              <span><b>Publish on the website</b> — leave unticked to save as a draft</span>
            </label>
            <div className="admin-actions">
              <Link to="/admin/packages" className="btn btn-outline">Cancel</Link>
              <button className="btn btn-primary" disabled={busy}>{busy ? 'Saving…' : 'Save package'}</button>
            </div>
          </div>
        </form>
      )}
    </AdminShell>
  );
}
